<script setup lang="ts">
import { ref, computed } from 'vue';
import { RouterView, RouterLink, useRoute } from 'vue-router';
import {
  LayoutDashboard, PackageOpen, Settings, Database,
  ChevronDown, ChevronRight, Binary, LogOut
} from 'lucide-vue-next';
import { useUserStore } from '@/store/useUserStore';

const route = useRoute();

const UserStore = useUserStore();

const title = import.meta.env.VITE_APP_TITLE;

// 메뉴 데이터
// key — HIDEABLE_MENUS와 매칭되는 필터링 키 (path 마지막 segment)
// adminOnly — Admin(role 1)에게만 노출
const menuItems = [
  // { icon: LayoutDashboard, label: "대시보드", path: "/" },
  { icon: PackageOpen, label: "릴리즈", key:'releases', path: "/releases" ,isGroup: true, },
  {
    icon: Binary,
    label: "바이너리",
    isGroup: true,
    children: [
      { key: 'apps', label: "앱", path: "/apps" },
      { key: 'firmwares', label: "펌웨어", path: "/firmwares" },
    ]
  },
  {
    icon: Database,
    label: "기본 데이터",
    isGroup: true,
    children: [
      { key: 'chipsets', label: "칩셋", path: "/chipsets" },
      { key: 'models', label: "모델", path: "/models" },
      { key: 'firmware-types', label: "펌웨어 타입", path: "/firmware-types" },
      { key: 'package-types', label: "패키지 타입", path: "/package-types" },
      { key: 'packages', label: "패키지", path: "/packages" },
      { key: 'priorities', label: "우선순위", path: "/priorities" },
    ]
  },
  { icon: Settings, 
    label: "설정",
    isGroup: true,
    children: [
      { key: 'settings', label: "일반", path: "/settings" },
      { key: 'users', label: "유저관리", path: "/users" , adminOnly: true},
      { key: 'roles', label: "권한관리", path: "/roles" , adminOnly: true},
    
  ]
  },
  { icon: LogOut, label: "로그아웃", path: "/logout" },
];

// 현재 사용자 권한에 따라 사이드바에 보일 메뉴만 필터링
const visibleMenuItems = computed(() => {
  const hidden: string[] = UserStore.menus ?? [];
  const isAdmin = UserStore.role === 1;
  return menuItems
    //유저관리는 Admin이 아닐 때 제외
    .filter((item: any) => !item.adminOnly || isAdmin)
    .map((item: any) => {
      if (item.isGroup && Array.isArray(item.children)) {
        return { ...item, children: item.children.filter((c: any) => hidden.includes(c.key)) };
      }
      return item;
    })
    .filter((item: any) => {
      if (item.key && hidden.includes(item.key)) return true;
      if (item.isGroup && (!item.children || item.children.length === 0)) return false;
      return true;
    });
});

const expandedGroups = ref<Record<string, boolean>>({
  "바이너리": true,
  "기본 데이터": true,
});

const toggleGroup = (label: string) => {
  expandedGroups.value[label] = !expandedGroups.value[label];
};

// 유틸리티 함수: 경로 활성화 체크
const isPathActive = (path: string) => {
  return route.path === path || route.path.startsWith(path + '/');
};

const hasActiveChild = (children: any[]) => {
  return children.some(child => isPathActive(child.path));
};
</script>

<template>
  <div class="flex h-screen bg-background" >
    <aside v-if="UserStore.isLoggedIn" class="w-64 border-r bg-card">
      <div class="flex h-full flex-col">
        <div class="flex h-16 items-center border-b px-6">
          <h1 class="text-xl font-semibold text-foreground">{{ title }}</h1>
        </div>

        <nav class="flex-1 space-y-1 p-4">
          <template v-for="item in visibleMenuItems" :key="item.label">
            <div v-if="item.isGroup && item.children">
              <button @click="toggleGroup(item.label)" :class="[
                'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                hasActiveChild(item.children)
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              ]">
                <div class="flex items-center gap-3">
                  <component :is="item.icon" class="h-5 w-5" />
                  {{ item.label }}
                </div>
                <component :is="expandedGroups[item.label] ? ChevronDown : ChevronRight" class="h-4 w-4" />
              </button>

              <div v-if="expandedGroups[item.label]" class="ml-8 mt-1 space-y-1">
                <RouterLink v-for="child in item.children" :key="child.path" :to="child.path" :class="[
                  'block rounded-lg px-3 py-2 text-sm transition-colors',
                  isPathActive(child.path)
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                ]">
                  {{ child.label }}
                </RouterLink>
              </div>
            </div>

            <RouterLink v-else :to="item.path!" @click="item.action ? item.action() : null" :class="[
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isPathActive(item.path!)
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'  
            ]"
            :disabled="item.disabled"
            >
              <component :is="item.icon" class="h-5 w-5" />
              {{ item.label }}
            </RouterLink>
          </template>
        </nav>

        <div class="border-t p-4 text-xs text-muted-foreground">
          <p>Version 1.0.0</p>
          <p class="mt-1">© 2026 Deployment System</p>
        </div>
      </div>
    </aside>

    <main class="flex-1 overflow-auto bg-background text-foreground">
      <RouterView />
    </main>
  </div>
</template>